// src/pages/EthicsPage.jsx
import { useEffect, useMemo, useState } from "react";
import { Banner } from "../../../components/common/Banner";
import * as Icons from "lucide-react";
import {
  getEthicsConfig,
  getEthicsContent,
  getLatestEthicsDoc,
  downloadEthicsDocUrl,
} from "../../../services/ethicsCodeService";

/* ---------- helpers ---------- */
const isPageUrl = (s) =>
  typeof s === "string" &&
  (s.startsWith("/") || s.startsWith("http://") || s.startsWith("https://"));

const linkPropsFor = (href) =>
  isPageUrl(href) ? { target: "_blank", rel: "noopener noreferrer" } : {};

const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
};

/* ---------- UI helpers ---------- */
const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-[#0B4A8F] border border-blue-100 px-3 py-1 text-sm font-medium">
    {children}
  </span>
);
const Dot = () => <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-300" />;

const Bullet = ({ children }) => <li className="pl-1">{children}</li>;

const resolveIcon = (name) => (name && Icons[name]) || Icons.FileText;

const Section = ({ title, icon: Icon = Icons.FileText, children }) => (
  <section className="rounded-2xl bg-white ring-1 ring-black/5 p-6">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-5 h-5 text-[#0B4A8F]" />
      <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
    </div>
    <div className="text-gray-700 leading-relaxed">{children}</div>
  </section>
);

/** Miniatura tipo Annual Reports / Crime: usa coverUrl o muestra "Sin portada" */
function CoverThumb({ src, pdfHref, alt = "Portada Código de Ética" }) {
  const [ok, setOk] = useState(Boolean(src));
  const canEmbed = Boolean(pdfHref);
  
  const absoluteUrl = (href) => {
    try {
      return new URL(href, window.location.origin).href;
    } catch {
      return href;
    }
  };
  
  return (
    <div className="w-[140px] h-[190px] sm:w-[160px] sm:h-[220px] md:w-[180px] md:h-[250px] rounded-xl border border-blue-100 bg-white overflow-hidden flex items-center justify-center shadow-sm">
      {ok && src ? (
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          onError={() => setOk(false)}
          loading="lazy"
        />
      ) : canEmbed ? (
        <embed
          src={`${absoluteUrl(pdfHref)}#page=1&zoom=105`}
          type="application/pdf"
          className="w-full h-full block"
        />
      ) : null}
    </div>
  );
}

/* ---------- Página ---------- */
export default function EthicsPage() {
  const [pageTitle, setPageTitle] = useState("Código de Ética");
  const [subtitle, setSubtitle] = useState("Nuestros principios, conductas esperadas y canal de integridad.");
  const [lastReview, setLastReview] = useState("");          // ← de /content
  const [lastVisibleAt, setLastVisibleAt] = useState(null);  // ← de /config
  const [principlesVisible, setPrinciplesVisible] = useState(true);  // ← de /config

  const [principles, setPrinciples] = useState([
    "Integridad",
    "Respeto",
    "Responsabilidad",
    "Transparencia",
    "Competencia leal",
    "Sostenibilidad",
  ]);
  const [sections, setSections] = useState([]);
  const [latestDoc, setLatestDoc] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        // Carga paralela
        const [cfg, content, latest] = await Promise.all([
          getEthicsConfig().catch(() => null),
          getEthicsContent().catch(() => null),
          getLatestEthicsDoc().catch(() => null),
        ]);

        if (cfg?.pageTitle) setPageTitle(String(cfg.pageTitle));
        setSubtitle(cfg?.subtitle ? String(cfg.subtitle) : "Nuestros principios, conductas esperadas y canal de integridad.");
        if (cfg?.lastVisibleAt) setLastVisibleAt(cfg.lastVisibleAt); // ← SOLO config
        setPrinciplesVisible(cfg?.principlesVisible !== undefined ? !!cfg.principlesVisible : true);

        if (content?.principles?.length) setPrinciples(content.principles);
        if (content?.sections?.length) setSections(content.sections);
        if (typeof content?.lastReview === "string") setLastReview(content.lastReview); // ← SOLO content

        if (latest?.id) setLatestDoc(latest);
      } catch {
        /* defaults */
      }
    })();
  }, []);

  // CTA global opcional (si alguna sección lo define)
  const globalCTA = useMemo(() => {
    for (const s of sections || []) {
      if (s?.cta?.reportUrl || s?.cta?.policyUrl) return s.cta;
    }
    return null;
  }, [sections]);

  const coverUrl =
    latestDoc?.coverUrl && String(latestDoc.coverUrl).trim().length > 0
      ? latestDoc.coverUrl
      : null;

  const docHref =
    typeof latestDoc?.secureUrl === "string" && latestDoc.secureUrl.trim()
      ? latestDoc.secureUrl
      : latestDoc?.id
      ? downloadEthicsDocUrl(latestDoc.id)
      : null;

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <Banner title={pageTitle} message={subtitle} className="mb-6" />

      {/* Badges de contexto (misma UX que Prevención) */}
      <div className="flex flex-wrap items-center gap-2 text-sm mb-6">
        {lastReview && <Pill>Última revisión: {lastReview}</Pill>}
        {latestDoc && lastVisibleAt && (
          <>
            <Dot />
            <Pill>Última actualización visible: {fmtDateTime(lastVisibleAt)}</Pill>
          </>
        )}
        {latestDoc?.versionTag && (
          <>
            <Dot />
            <Pill>Versión PDF: {latestDoc.versionTag}</Pill>
          </>
        )}
      </div>

      {/* Documento + portada + CTA */}
      {latestDoc && (
        <div className="rounded-3xl bg-gradient-to-t from-[#0EC5DF] via-[#0488AF] to-[#0B4A8F] text-white px-5 py-6 md:px-7 md:py-7 mb-10 shadow-sm">
          <div className="grid gap-5 md:gap-7 md:grid-cols-[minmax(240px,280px),minmax(0,1fr)] items-start">
            {/* Portada */}
            <div className="w-full max-w-[280px] justify-self-center md:justify-self-start">
              <div className="aspect-[3/4] rounded-2xl bg-white overflow-hidden shadow-md">
                {coverUrl ? (
                  <img
                    className="w-full h-full object-cover"
                    src={coverUrl}
                    alt="Portada Código de Ética"
                    onError={(e) => {
                      if (docHref) {
                        const href = typeof docHref === 'string' ? docHref : '';
                        e.currentTarget.parentElement.innerHTML = `<embed type="application/pdf" src="${href}#page=1&zoom=125" style="width:100%;height:100%;display:block;border:0" />`;
                      }
                    }}
                  />
                ) : docHref ? (
                  <embed
                    type="application/pdf"
                    src={`${docHref}#page=1&zoom=125`}
                    style={{ width: '100%', height: '100%', display: 'block', border: 0 }}
                  />
                ) : null}
              </div>
            </div>

            {/* Título + botón */}
            <div className="w-full min-w-0">
              <div className="text-xl md:text-2xl font-semibold mb-3 text-white">
                {latestDoc.title?.trim() || "Código de Ética"}
              </div>
              {docHref && (
                <a
                  href={docHref}
                  {...linkPropsFor(docHref)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/95 text-[#0488AF] hover:bg-white font-medium shadow-sm"
                >
                  <Icons.FileDown className="w-4 h-4" />
                  Ver / Descargar Código de Ética
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Principios */}
      {principlesVisible && principles?.length > 0 && (
        <div className="rounded-2xl bg-white ring-1 ring-black/5 p-5 mb-6">
          <div className="text-sm font-semibold text-gray-700 mb-3">Nuestros principios</div>
          <div className="flex flex-wrap gap-2">
            {principles.map((p, i) => (
              <Pill key={i}>{p}</Pill>
            ))}
          </div>
        </div>
      )}

      {/* Secciones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {(sections.length
          ? sections
          : [
              {
                title: "Propósito y alcance",
                icon: "ShieldCheck",
                body: [
                  "Define estándares de conducta para directorio, colaboradores y terceros.",
                  "Aplica en todas las operaciones y complementa leyes y políticas internas.",
                ],
              },
              {
                title: "Cumplimiento y anticorrupción",
                icon: "HandCoins",
                bullets: [
                  "Prohibición de sobornos y pagos de facilitación.",
                  "Relación con autoridades conforme a la ley y controles internos.",
                  "Registro fiel y oportuno de transacciones.",
                ],
              },
              {
                title: "Canal de integridad (denuncias y consultas)",
                icon: "Megaphone",
                cta: {
                  reportUrl: "mailto:etica@tu-empresa.cl",
                  policyUrl: "/politicas/linea-de-etica",
                },
              },
            ]).map((sec, i) => {
          const Icon = resolveIcon(sec.icon);
          return (
            <Section key={i} title={sec.title} icon={Icon}>
              {Array.isArray(sec.body) &&
                sec.body.map((txt, j) => (
                  <p className="mb-2" key={j}>
                    {txt}
                  </p>
                ))}
              {Array.isArray(sec.bullets) && (
                <ul className="list-disc pl-6 space-y-1">
                  {sec.bullets.map((b, j) => (
                    <Bullet key={j}>{b}</Bullet>
                  ))}
                </ul>
              )}
              {sec.cta && (sec.cta.reportUrl || sec.cta.policyUrl || sec.cta.contactUrl) && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {sec.cta.reportUrl && (
                    <a
                      href={sec.cta.reportUrl}
                      {...linkPropsFor(sec.cta.reportUrl)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0488AF] text-white hover:bg-[#037a98]"
                    >
                      <Icons.Megaphone className="w-4 h-4" />
                      Reportar una inquietud
                    </a>
                  )}
                  {sec.cta.policyUrl && (
                    <a
                      href={sec.cta.policyUrl}
                      {...linkPropsFor(sec.cta.policyUrl)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border bg-white hover:bg-gray-50"
                    >
                      <Icons.ExternalLink className="w-4 h-4" />
                      Ver política
                    </a>
                  )}
                  {sec.cta.contactUrl && (
                    <a
                      href={sec.cta.contactUrl}
                      {...linkPropsFor(sec.cta.contactUrl)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0488AF] text-white hover:bg-[#037a98]"
                    >
                      {/^mailto:/i.test(sec.cta.contactUrl) ? (
                        <Icons.Mail className="w-4 h-4" />
                      ) : (
                        <Icons.ExternalLink className="w-4 h-4" />
                      )}
                      Contacto
                    </a>
                  )}
                </div>
              )}
            </Section>
          );
        })}
      </div>
    </div>
  );
}
